import { useCallback, useRef, useState } from "react";

export function useCamera() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraError, setCameraError] = useState(null);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("This browser blocks camera access. Open the app in a normal browser tab, or use Upload.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      const name = e?.name || "";
      if (["NotAllowedError", "SecurityError", "PermissionDeniedError"].includes(name)) {
        setCameraError("Camera permission is blocked. Tap the camera/lock icon in the address bar, choose Allow, then Retry.");
      } else if (["NotFoundError", "DevicesNotFoundError", "OverconstrainedError"].includes(name)) {
        setCameraError("No camera found. Use Upload, or switch to a phone/laptop with a camera.");
      } else if (name === "NotReadableError") {
        setCameraError("Camera is in use by another app. Close it and press Retry.");
      } else {
        setCameraError("Camera unavailable. Use Upload, or press Retry.");
      }
    }
  }, []);

  const capture = useCallback((quality = 0.85) => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    return canvas.toDataURL("image/jpeg", quality);
  }, []);

  return { videoRef, cameraError, start, stop, capture };
}
